import React, { useContext, useEffect, useState } from 'react';
import { DndProvider, DropTargetMonitor, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import styled from 'styled-components';
import DropdownSelect from '../../../components/DropdownSelect';
import ConfirmModal from '../../../components/modal/ConfirmModal';
import ProductController, {
  OptionGroupItemType,
  OptionGroupType,
} from '../../../controller/ProductController';
import { useToast } from '../../../controller/ToastController';
import { AuthContext } from '../../../navigation/AuthProvider';
import { getNumberToString } from '../../../utils/util';
import { CurrentStoreContext } from '../../../controller/StoreController';

const requiredNumList: any = [];
for (let i = 1; i < 11; i++) {
  requiredNumList.push({
    key: i,
    value: i,
    name: `${i}개`,
    label: `${i}개`,
  });
}

const OptionEdit = () => {
  const navigate = useNavigate();
  const { currentStore } = useOutletContext<CurrentStoreContext>();
  const location = useLocation();
  const optionInfo: OptionGroupType = location.state?.optionInfo;
  const showToast = useToast();

  const [name, setName] = useState<string>('');
  const [optionList, setOptionList] = useState<OptionGroupItemType[]>([]);
  const [optionName, setOptionName] = useState<string>('');
  const [optionPrice, setOptionPrice] = useState<string>('');
  const [isRequired, setIsRequired] = useState<boolean>(true);
  const [min, setMin] = useState({
    key: 1,
    value: 1,
    name: '1개',
    label: '1개',
  });
  const [max, setMax] = useState({
    key: 0,
    value: 0,
    name: '최대 선택수를 선택해주세요',
    label: `최대 선택수를 선택해주세요`,
  });

  const [visibleConfirm, setVisibleConfirm] = useState<boolean>(false);

  const deleteOption = () => {
    deleteOptionData();
    setVisibleConfirm(false);
  };

  const onChange_isRequired = (required: boolean) => {
    if (required) {
      setMin({
        key: 1,
        value: 1,
        name: '1개',
        label: '1개',
      });
      setIsRequired(true);
    } else {
      setMin({
        key: 0,
        value: 0,
        name: '0개',
        label: '0개',
      });
      setIsRequired(false);
    }
  };
  const onChange_name = (event: any) => {
    setName(event.target.value);
  };
  const onChange_optionName = (event: any) => {
    setOptionName(event.target.value);
  };
  const onChange_optionPrice = (event: any) => {
    setOptionPrice(getNumberToString(event.target.value));
  };
  const onChange_min = (event: any) => {
    setMin(event);
  };
  const onChange_max = (event: any) => {
    setMax(event);
  };

  const addNewOptionItem = () => {
    if (optionName.length > 0) {
      const optionItemInfo: OptionGroupItemType = {
        name: optionName,
        price: optionPrice === '' ? 0 : parseInt(optionPrice),
        description: '',
      };
      optionList.push(optionItemInfo);
      setOptionName('');
      setOptionPrice('');
    } else showToast('정확한 정보를 입력해주세요', 'error');
  };

  const deleteOptionItem = (index: number) => {
    optionList.splice(index, 1);
    setOptionList([...optionList]);
  };

  // 필수 확인
  const checkRequired = () => {
    if (
      name.length > 0 &&
      max.value > 0 &&
      min.value <= optionList.length &&
      max.value <= optionList.length &&
      min.value <= max.value
    )
      return true;
    return false;
  };

  // API 호출
  const setOptionData = async () => {
    try {
      if (checkRequired()) {
        if (optionInfo) {
          const optionGroup = {
            name: name,
            option_type_id: optionInfo.option_type_id,
            input_min: min.value,
            input_max: max.value,
            option_list: optionList,
          };
          const oRes = await ProductController.editOptionGroup(currentStore.value, optionGroup);
          if (oRes && oRes.data) {
            const oResponse = oRes.data;
            console.log('editOptionGroup', oResponse);
            if (oResponse.data && oResponse.ok) {
              // console.log(optionGroup);
              showToast('새로운 옵션 정보를 등록했어요');
            }
          }
        } else {
          const optionGroup = {
            name: name,
            input_min: min.value,
            input_max: max.value,
            option_list: optionList,
          };
          const oRes = await ProductController.setOptionGroup(currentStore.value, optionGroup);
          if (oRes && oRes.data) {
            const oResponse = oRes.data;
            console.log('setOptionGroup', oResponse);
            if (oResponse.data && oResponse.ok) {
              // console.log(optionGroup);
              showToast('새로운 옵션 정보를 등록했어요');
              navigate(-1);
            }
          }
        }
      } else {
        if (min.value > optionList.length || max.value > optionList.length)
          showToast('선택수가 옵션 개수보다 많아요', 'error');
        else if (min.value > max.value) {
          showToast('필수 선택수가 최대 선택수보다 많아요', 'error');
        } else showToast('필수 정보를 입력해주세요', 'error');
      }
    } catch (err) {
      console.log('err---->', err);
    }
  };
  const deleteOptionData = async () => {
    try {
      const oRes = await ProductController.delOptionGroup(optionInfo.option_type_id);
      if (oRes && oRes.data) {
        const oResponse = oRes.data;
        console.log('delOptionGroup', oResponse);
        if (oResponse.data && oResponse.ok) {
          showToast('옵션을 삭제했어요');
          navigate(-1);
        }
      }
    } catch (err) {
      console.log('err---->', err);
    }
  };

  useEffect(() => {
    if (optionInfo) {
      console.log('editOptionInfo', optionInfo);
      setName(optionInfo.name);
      setMin({
        key: optionInfo.input_min,
        value: optionInfo.input_min,
        name: `${optionInfo.input_min}개`,
        label: `${optionInfo.input_min}개`,
      });
      setMax({
        key: optionInfo.input_max,
        value: optionInfo.input_max,
        name: `${optionInfo.input_max}개`,
        label: `${optionInfo.input_max}개`,
      });
      setOptionList([...optionInfo.option_list]);
      setIsRequired(optionInfo.input_min !== 0);
    }
  }, []);

  interface DraggableOptionItemProps {
    item: OptionGroupItemType;
    index: number;
    moveItem: (fromIndex: number, toIndex: number) => void;
  }

  const moveItem = (fromIndex: number, toIndex: number) => {
    if (toIndex < 1) toIndex = 0;
    else if (toIndex > optionList.length - 1) toIndex = optionList.length;
    const newItems = [...optionList];
    const [movedItem] = newItems.splice(fromIndex, 1);
    newItems.splice(toIndex, 0, movedItem);
    setOptionList(newItems);
  };

  const RenderOptionItem = ({ item, index, moveItem }: DraggableOptionItemProps) => {
    const [selectedIndex, setSelectedIndex] = useState<number>(index + 1);
    const [isEdit, setIsEdit] = useState<boolean>(false);
    const [itemName, setItemName] = useState<string>(item.name);
    const [itemPrice, setItemPrice] = useState<number>(item.price);

    const onChange_name = () => {
      let optionListTemp: OptionGroupItemType[] = optionList;
      optionListTemp[index].name = itemName;
      setOptionList([...optionListTemp]);
    };
    const onChange_price = () => {
      let optionListTemp: OptionGroupItemType[] = optionList;
      optionListTemp[index].price = itemPrice;
      setOptionList([...optionListTemp]);
    };

    const [, drag] = useDrag({
      type: 'ITEM',
      item: { item, index },
    });

    const [, drop] = useDrop({
      accept: 'ITEM',
      hover: (item: { index: number }, monitor: DropTargetMonitor) => {
        if (item.index !== index) {
          moveItem(item.index, index);
          item.index = index;
        }
      },
    });

    return (
      <div className="optionItem" key={index}>
        <div className="row gap">
          <input
            ref={(node) => drag(drop(node))}
            value={selectedIndex}
            autoFocus={false}
            className="indexInput"
            onChange={(e) => {
              setSelectedIndex(Number(e.target.value));
            }}
            onBlur={() => {
              moveItem(index, selectedIndex - 1);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                moveItem(index, selectedIndex - 1);
              }
            }}
          />
          <input
            className={`nameInput ${isEdit && 'active'}`}
            type="text"
            value={itemName}
            autoFocus={false}
            onChange={(e: any) => setItemName(e.target.value)}
            disabled={!isEdit}
            onBlur={onChange_name}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                onChange_name();
              }
            }}
          />
        </div>
        <div className="row gap">
          <div className="priceWrap">
            <div className="unit">원</div>
            <input
              className={`priceInput ${isEdit && 'active'}`}
              type="text"
              value={`${itemPrice}`}
              autoFocus={false}
              onChange={(e: any) => setItemPrice(e.target.value)}
              disabled={!isEdit}
              onBlur={onChange_price}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  onChange_price();
                }
              }}
            />
          </div>
          <img
            src={`${process.env.PUBLIC_URL}/svg/ic_edit.svg`}
            alt="ic"
            onClick={() => setIsEdit(!isEdit)}
          />
          <img
            src={`${process.env.PUBLIC_URL}/svg/ic_trashcan.svg`}
            alt="ic"
            onClick={() => deleteOptionItem(index)}
          />
        </div>
      </div>
    );
  };

  return (
    <>
      <Style>
        <div className="contentContainer">
          <div className="editHeaderWrap">
            <button className="backButton" onClick={() => navigate(-1)}>
              <img src={`${process.env.PUBLIC_URL}/svg/ic_arrow_left.svg`} alt="ic" />
            </button>
            {optionInfo && (
              <button className="deleteButton" onClick={() => setVisibleConfirm(true)}>
                <img src={`${process.env.PUBLIC_URL}/svg/ic_trashcan_green.svg`} alt="ic" />
                <h5>옵션그룹 삭제</h5>
              </button>
            )}
          </div>

          <div className="inputText">
            <div className="title">
              <h4>옵션 그룹명</h4>
              <h5>(필수)</h5>
            </div>
            <input
              type="text"
              placeholder="메뉴명을 적어주세요"
              value={name}
              onChange={onChange_name}
              autoFocus={true}
            />
          </div>
          <div className="inputText">
            <div className="title">
              <h4>선택 필수 여부</h4>
              <h5>(필수)</h5>
            </div>
            <div className="selectBoxWrap">
              <div
                className={`selectBox left ${isRequired && 'active'}`}
                onClick={() => onChange_isRequired(true)}>
                필수
              </div>
              <div
                className={`selectBox right ${!isRequired && 'active'}`}
                onClick={() => onChange_isRequired(false)}>
                필수없음
              </div>
            </div>
          </div>
          {isRequired && (
            <div className="inputText">
              <div className="title">
                <h4>필수 선택수</h4>
              </div>
              <DropdownSelect
                options={requiredNumList}
                defaultValue={min}
                onChangeValue={onChange_min}
              />
            </div>
          )}
          <div className="inputText">
            <div className="title">
              <h4>최대 선택수</h4>
              <h5>(필수)</h5>
            </div>
            <DropdownSelect
              options={requiredNumList}
              defaultValue={max}
              onChangeValue={onChange_max}
            />
          </div>
        </div>

        <div className="contentContainer">
          <div className="inputText">
            <div className="title initial">
              <h4>옵션명</h4>
            </div>
            <div className="doubleInputWrap">
              <input
                className="optionNameWrap"
                type="text"
                placeholder="옵션명을 적어주세요"
                value={optionName}
                onChange={onChange_optionName}
                autoFocus={false}
              />
              <div className="inputWrap">
                <div className="unit">원</div>
                <input
                  type="text"
                  value={optionPrice}
                  onChange={onChange_optionPrice}
                  placeholder="0"
                  autoFocus={false}
                />
              </div>
            </div>
          </div>
          <button className="addButton" onClick={addNewOptionItem}>
            <img src={`${process.env.PUBLIC_URL}/svg/ic_plus.svg`} alt="ic" />
            <h5>새 옵션 추가하기</h5>
          </button>
          <div className="warningSign">
            <img src={`${process.env.PUBLIC_URL}/svg/ic_warning.svg`} alt="ic" />
            <p>숫자를 입력하거나 숫자 버튼을 드래그 해서 순서를 변경해주세요. </p>
          </div>
          <div className="inputText">
            <DndProvider backend={HTML5Backend}>
              <div className="optionList">
                {optionList.map((item: OptionGroupItemType, index: number) => {
                  return (
                    <RenderOptionItem key={index} item={item} index={index} moveItem={moveItem} />
                  );
                })}
              </div>
            </DndProvider>
          </div>
          <button
            className={`submitButton ${!checkRequired() && 'inactive'}`}
            onClick={setOptionData}>
            저장
          </button>
        </div>
      </Style>
      <ConfirmModal
        isOpen={visibleConfirm}
        title={`삭제 후 복구할 수 없습니다.\n정말 삭제하시겠어요?`}
        onCancelText="취소"
        onConfirmText="삭제"
        onCancel={() => setVisibleConfirm(false)}
        onConfirm={deleteOption}
      />
    </>
  );
};

export default OptionEdit;

const Style = styled.div`
  .contentContainer:nth-child(1) {
    border-radius: 10px 10px 0 0;
  }
  .contentContainer:nth-child(2) {
    border-radius: 0 0 10px 10px;
  }

  @keyframes expandDown {
    0% {
      height: 0;
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }

  .row.gap {
    gap: 10px;
  }

  .editHeaderWrap {
    margin-left: -50px;
    display: flex;
    align-items: center;
    justify-content: space-between;

    .deleteButton {
      display: flex;
      align-items: center;
      padding: 6px 10px;
      border: 1px solid #40977f;
      border-radius: 5px;
      gap: 10px;
      h5 {
        color: #40977f;
      }
    }
  }

  .addButton {
    width: 100%;
    border: 1px solid #4dd2ae;
    border-radius: 5px;
    padding: 8px 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 5%;
    margin: 10px 0;

    h5 {
      font-weight: 600;
      color: #4dd2ae;
      white-space: nowrap;
      padding: 0;
    }
  }

  .inputText {
    img {
      cursor: pointer;
    }

    .title {
      padding: 30px 0 6px 0;
      display: flex;
      align-items: center;
      gap: 6px;

      h5 {
        color: #40977f;
        font-weight: 500;
      }
      .imageSpan {
        color: #979797;
        font-weight: 400;
      }
      h4 {
        color: #555555;
      }
    }
    .title.initial {
      padding: 0 0 6px 0;
    }
    .optionNameWrap {
      flex: 7;
    }

    .inputWrap {
      flex: 3;
      width: 100%;
      position: relative;
      .unit {
        position: absolute;
        font-size: 16px;
        line-height: 22px;
        color: #a7a7a7;
        top: 50%;
        transform: translateY(-50%);
        right: 14px;
      }
    }
  }

  input,
  textarea {
    width: 100%;
    padding: 10px 12px;
    border: 1px solid #cecece;
    border-radius: 5px;
    color: #979797;
  }
  input.optionName {
    flex: 3;
  }
  input.optionPrice {
    flex: 1;
  }

  input::placeholder,
  textarea::placeholder {
    color: #a7a7a7;
  }

  textarea {
    min-height: 120px;
  }

  .doubleInputWrap {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .optionList {
    display: flex;
    flex-direction: column;
    padding: 30px 0;
    gap: 10px;

    .optionItem {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 8px 0;

      .row.gap {
        gap: 16px;
      }

      .indexInput {
        width: 24px;
        height: 24px;
        padding: 4px;
        font-size: 14px;
        line-height: 16px;
        color: #979797;
        background: #ffffff;
        border: 1px solid #cecece;
        text-align: center;
        border-radius: 3px;
      }

      .nameInput {
        font-size: 18px;
        line-height: 24px;
        background: #ffffff;
        color: #555555;
        font-weight: 600;
        text-align: left;
        padding: 0;
        border: 2px solid #ffffff;
        border-radius: 0;
      }
      input.active {
        border-bottom: 2px solid #4dd2ae;
      }
      .priceInput {
        font-size: 14px;
        line-height: 20px;
        background: #ffffff;
        color: #555555;
        font-weight: 400;
        text-align: right;
        padding: 0 16px 0 0;
        border: 2px solid #ffffff;
        border-radius: 0;
      }
      .input:disabled {
        background: #ffffff;
      }

      .priceWrap {
        display: flex;
        align-items: center;
        padding-right: 60px;
        position: relative;
        .unit {
          position: absolute;
          font-size: 14px;
          line-height: 20px;
          color: #555555;
          top: 50%;
          transform: translateY(-50%);
          right: 64px;
        }
      }

      h4 {
        color: #555555;
        font-weight: 600;
      }
      h5 {
        color: #555555;
        font-weight: 400;
      }
    }
  }

  .selectBoxWrap {
    display: flex;
    align-items: center;

    .selectBox {
      flex: 1;
      border: 1px solid #cecece;
      background: #ffffff;
      font-size: 16px;
      line-height: 24px;
      color: #555555;
      padding: 6px 0;
      text-align: center;
      cursor: pointer;
    }
    .selectBox.left {
      border-radius: 5px 0px 0px 5px;
      border-right: none;
    }
    .selectBox.right {
      border-radius: 0px 5px 5px 0px;
      border-left: none;
    }
    .selectBox.active {
      border: 1px solid #40977f;
      background: #ecf5f3;
      color: #40977f;
    }
  }

  @media (max-width: 900px) {
    .doubleInputWrap {
      flex-direction: column;
    }
  }
`;
